import { FormattedMessage } from 'react-intl';
import StatusDropdown, { DealStatus } from '../statusDropdown/statusDropdown';
import { DealUserRoles } from '../../../../shared/hooks/useDealRoles/useDealRoles';
import { dealStatusesForStatusDropdown } from '../../constants/DealConstants';
import { Row as RowTable } from 'react-table';
import dayjs from 'dayjs';
import React from 'react';
import DealNotificationToggle from '../dealNotification/DealNotificationToggle';
import DealControls from '../DealControls';
import { ReactComponent as FolderIcon } from '../../../../shared/icons/folder-icon.svg';
import DealFolderControls from '../DealFolderControls';

type ColumnsCellType = {
  cell: {
    value: DealStatus;
    row: {
      original: { currentUserRole: DealUserRoles; id: string };
    };
  };
};

type Props = {
  canEdit: boolean;
  handleUpdateDealDetails: (sealId: string, newStatus: DealStatus) => void;
  userDealNotifications: any;
  refetchUser: any;
  teamId: string;
  onSuccess: () => void;
};

export const dealTableColumns = ({
  canEdit,
  handleUpdateDealDetails,
  userDealNotifications,
  refetchUser,
  teamId,
  onSuccess,
}: Props) => [
  {
    Header: <FormattedMessage id="title.deals.title" defaultMessage="Name" />,
    accessor: 'title',
    Cell: ({ cell: { value }, row: { original } }: any) => {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {original.isFolder && (
            <FolderIcon
              style={{
                marginRight: '8px',
                width: '20px',
                height: '20px',
                fill: '#006c90',
              }}
            />
          )}
          <div>
            <div>{value}</div>
            {original.folderPath && (
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>{original.folderPath}</div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    Header: <FormattedMessage id="title.deals.deal-status" defaultMessage="Deal Creator" />,
    accessor: 'creator',
  },
  {
    Header: <FormattedMessage id="title.deals.deal-status" defaultMessage="Deal Status" />,
    accessor: 'status',
    width: 70,
    Cell: ({
      cell: {
        value: status,
        row: {
          original: { currentUserRole, id },
        },
      },
    }: ColumnsCellType) => {
      if (currentUserRole === DealUserRoles.CREATOR && !!canEdit) {
        return (
          <StatusDropdown
            currentStatus={status}
            onStatusChange={(newStatus: DealStatus) => {
              handleUpdateDealDetails(id, newStatus);
            }}
          />
        );
      }

      return (
        <FormattedMessage
          id="phrase.deal-status-readout"
          defaultMessage={dealStatusesForStatusDropdown}
          values={{ status }}
        />
      );
    },
  },
  {
    Header: <FormattedMessage id="title.deals.last-updated" defaultMessage="Last Updated" />,
    accessor: 'updatedAt',
    width: 150,
    sortType: (a: RowTable<{ values: { updatedAt: string } }>, b: RowTable<{ values: { updatedAt: string } }>) => {
      return new Date(b.values.updatedAt).getTime() - new Date(a.values.updatedAt).getTime();
    },
    Cell: ({ cell: { value } }: any) => {
      return <span className="text-nowrap">{value && dayjs(value).format('LL')}</span>;
    },
  },
  {
    accessor: 'actions',
    width: 100,
    Cell: ({ row: { original } }: any) => {
      return (
        <div
          className="d-flex align-items-center justify-content-end"
          style={{ gap: '0.5rem' }}
          onClick={(evt: React.MouseEvent) => {
            evt.stopPropagation();
          }}
        >
          {original.isFolder ? (
            <DealFolderControls
              folderId={original.id}
              folderName={original.title}
              editPermission={canEdit}
              managePermission={canEdit}
              teamId={teamId}
              onSuccess={onSuccess}
            />
          ) : (
            <>
              <DealNotificationToggle
                dealId={original.id}
                userDealNotifications={userDealNotifications}
                refetchUser={refetchUser}
              />
              {/* Only show three dots menu for creators, not counterparties */}
              {original.currentUserRole === 'creator' && (
                <DealControls
                  dealId={original.id}
                  dealTitle={original.title}
                  currentFolderId={original.folder?.id}
                  editPermission={canEdit}
                  managePermission={canEdit}
                  teamId={teamId}
                  onSuccess={onSuccess}
                />
              )}
            </>
          )}
        </div>
      );
    },
  },
];
